import { useEffect, useState } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsLMS';
import { API_BASE } from '../../../../../api';
import axios from 'axios';
import AlertDialog from '../../Shared/AlertDialog';

const EMPTY = {
    idEstudiante: 0,
    idCurso: 0,
    calificacionFinal: 0,
    idEstado: 0,
    certificadoGenerado: false,
    ultimoAcceso: '',
    fechaTermino: '',
    activo: true,
};

export default function EstudiantesCursosModal({
    open,
    mode = 'new',
    data = null,
    onClose,
    onSubmit,
    inline = false,
    fixedStudentId = null,
    triggerAlert,
}) {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({
        idEstudiante: '',
        idCurso: '',
        idEstado: '',
    });

    // Modal de alertas estándar LMS
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });

    const [catCursos, setCatCursos] = useState([]);
    const [catEstudiantes, setCatEstudiantes] = useState([]);
    const [catEstados, setCatEstados] = useState([]);
    const [loadingCatalogs, setLoadingCatalogs] = useState(false);

    // -------- VALIDACIONES --------
    const validateField = (field, rawValue) => {
        const value = Number(rawValue) || 0;
        let error = '';

        switch (field) {
            case 'idEstudiante':
                if (value <= 0) error = 'Selecciona un estudiante.';
                break;
            case 'idCurso':
                if (value <= 0) error = 'Selecciona un curso.';
                break;
            case 'idEstado':
                if (value <= 0) error = 'Selecciona un estado.';
                break;
            default:
                break;
        }

        setErrors((prev) => ({ ...prev, [field]: error }));
        return !error;
    };

    const validateAll = () => {
        const okEst = validateField('idEstudiante', form.idEstudiante);
        const okCur = validateField('idCurso', form.idCurso);
        const okEdo = validateField('idEstado', form.idEstado);
        return okEst && okCur && okEdo;
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        if (field === 'idEstudiante' || field === 'idCurso' || field === 'idEstado') {
            validateField(field, value);
        }
    };

    const handleSubmitInternal = () => {
        const isValid = validateAll();
        if (!isValid) return;
        onSubmit(form, mode);
    };

    // -------- EFECTOS --------
    useEffect(() => {
        if (mode === 'edit' && data) {
            setForm({
                idEstudiante: data.idEstudiante ?? 0,
                idCurso: data.idCurso ?? 0,
                calificacionFinal: data.calificacionFinal ?? 0,
                idEstado: data.idEstado ?? 0,
                certificadoGenerado: data.certificadoGenerado ?? false,
                ultimoAcceso: data.ultimoAcceso ? (data.ultimoAcceso.split('T')[0]) : '', // Solo fecha YYYY-MM-DD
                fechaTermino: data.fechaTermino ? (data.fechaTermino.split('T')[0]) : '',
                activo: data.activo ?? true,
            });

            setErrors({
                idEstudiante: '',
                idCurso: '',
                idEstado: '',
            });
        } else {
            // Modo new
            setForm({
                ...EMPTY,
                idEstudiante: fixedStudentId ? Number(fixedStudentId) : 0
            });
            setErrors({
                idEstudiante: fixedStudentId ? '' : '',
                idCurso: '',
                idEstado: '',
            });
        }
    }, [mode, data, fixedStudentId]);

    const loadCatalogs = async () => {
        try {
            setLoadingCatalogs(true);
            const [resCur, resEst, resEdo, resEmp, resPers] = await Promise.all([
                axios.get(ENDPOINTS.cursos),
                axios.get(ENDPOINTS.estudiante),
                axios.get(ENDPOINTS.catEstados),
                axios.get(`${API_BASE}/Empleados`),
                axios.get(`${API_BASE}/Personas`),
            ]);

            setCatCursos((resCur.data || []).map(Models.cursos));
            setCatEstados((resEdo.data || []).map(Models.estados));

            // Resolver nombres de estudiantes
            const rawEst = (resEst.data || []).map(Models.estudiante);
            const rawEmp = (resEmp.data || []).map(Models.empleado);
            const rawPers = (resPers.data || []).map(Models.personas);

            const estudiantesEnriched = rawEst.map(est => {
                let fullName = `Estudiante #${est.idEstudiante}`;
                const emp = rawEmp.find(x => x.idEmpleado === est.idEmpleado);
                if (emp) {
                    const p = rawPers.find(x => x.idPersona === emp.idPersona);
                    if (p) fullName = `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim();
                }
                return { ...est, nombreCompleto: fullName };
            });
            setCatEstudiantes(estudiantesEnriched);

        } catch (e) {
            console.error(e);
            if (triggerAlert) {
                triggerAlert('Error al obtener catálogos');
            } else {
                setAlertDialog({
                    open: true,
                    title: 'Error de Catálogos',
                    message: 'Error al obtener catálogos',
                    variant: 'error'
                });
            }
        } finally {
            setLoadingCatalogs(false);
        }
    };

    useEffect(() => {
        if (open) loadCatalogs();
    }, [open]);

    if (!open) return null;

    // -------- UI --------
    const errorStyle = {
        color: '#b91c1c',
        fontSize: '.8rem',
        display: 'block',
        marginTop: '4px',
    };

    const isSaveDisabled = loadingCatalogs;

    const Header = (
        <div
            className="roles-topbar"
            style={{
                marginBottom: '0.6rem',
                paddingRight: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flex: '0 0 auto',
            }}>
            <h3 className="roles-title" style={{ marginRight: 0 }}>
                {mode === 'new' ? 'Nueva relación Estudiante - Curso' : 'Editar relación'}
            </h3>
            <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
        </div>
    );

    const Body = (
        <div
            style={{
                paddingLeft: '15%',
                paddingRight: 16,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.8rem',
                overflowY: 'auto',
                flex: '1 1 auto',
                minHeight: 0,
            }}>

            {/* Estudiante */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Estudiante:</label>
                <br /><br />
                <select
                    className={`infoField${errors.idEstudiante ? ' infoField--error' : ''}`}
                    value={form.idEstudiante}
                    onChange={e => handleChange('idEstudiante', Number(e.target.value))}
                    disabled={!!fixedStudentId}
                >
                    <option value={0}>— Selecciona estudiante —</option>
                    {catEstudiantes.map((item) => (
                        <option key={item.idEstudiante} value={item.idEstudiante}>
                            {item.nombreCompleto}
                        </option>
                    ))}
                </select>
                {errors.idEstudiante && <small style={errorStyle}>{errors.idEstudiante}</small>}
            </div>

            {/* Curso */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Curso:</label>
                <br /><br />
                <select
                    className={`infoField${errors.idCurso ? ' infoField--error' : ''}`}
                    value={form.idCurso}
                    onChange={e => handleChange('idCurso', Number(e.target.value))}
                >
                    <option value={0}>— Selecciona curso —</option>
                    {catCursos.map((item) => (
                        <option key={item.idCurso} value={item.idCurso}>
                            {item.nombre}
                        </option>
                    ))}
                </select>
                {errors.idCurso && <small style={errorStyle}>{errors.idCurso}</small>}
            </div>

            {/* Calificación */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Calificación Final (0-100):</label>
                <br /><br />
                <input
                    type="number"
                    className="infoField"
                    value={form.calificacionFinal}
                    onChange={(e) => handleChange('calificacionFinal', e.target.value)}
                    min={0}
                    max={100}
                />
            </div>

            {/* Estado */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Estado:</label>
                <br /><br />
                <select
                    className={`infoField${errors.idEstado ? ' infoField--error' : ''}`}
                    value={form.idEstado}
                    onChange={e => handleChange('idEstado', Number(e.target.value))}
                >
                    <option value={0}>— Selecciona estado —</option>
                    {catEstados.map((item) => (
                        <option key={item.idEstado} value={item.idEstado}>
                            {item.nombre}
                        </option>
                    ))}
                </select>
                {errors.idEstado && <small style={errorStyle}>{errors.idEstado}</small>}
            </div>

            {/* Último Acceso */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Último Acceso:</label>
                <br /><br />
                <input
                    type="date"
                    className="infoField"
                    value={form.ultimoAcceso}
                    onChange={(e) => handleChange('ultimoAcceso', e.target.value)}
                />
            </div>

            {/* Fecha de Término */}
            <div>
                <label className="roles-title" style={{ fontSize: '.95rem' }}>Fecha de Término:</label>
                <br /><br />
                <input
                    type="date"
                    className="infoField"
                    value={form.fechaTermino}
                    onChange={(e) => handleChange('fechaTermino', e.target.value)}
                />
            </div>

            {/* Flags */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', paddingTop: '1.5rem' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.9rem', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={form.certificadoGenerado}
                        onChange={(e) => handleChange('certificadoGenerado', e.target.checked)}
                        style={{ width: 'auto', marginRight: '8px' }}
                    />
                    Certificado Generado
                </label>
            </div>

        </div>
    );

    const Footer = (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '.6rem',
                marginTop: '.8rem',
                paddingRight: 16,
                flex: '0 0 auto',
            }}
        >
            <button className="roles-btn" onClick={onClose}>Cancelar</button>
            <button
                className="btn-action"
                onClick={handleSubmitInternal}
                disabled={isSaveDisabled}
                style={{
                    opacity: isSaveDisabled ? 0.5 : 1,
                    cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
                }}
            >
                {mode === 'new' ? 'Guardar' : 'Actualizar'}
            </button>
        </div>
    );

    if (inline) {
        return (
            <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {Header}
                {Body}
                {Footer}
            </div>
        );
    }

    return (
        <div
            className="roles-confirm-overlay"
            role="dialog"
            aria-modal="true"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
                zIndex: 1000,
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div
                className="roles-confirm-card"
                style={{
                    width: 'min(720px, 96vw)',
                    maxHeight: '85vh',
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(0,0,0,.25)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                {Header}
                {Body}
                {Footer}
            </div>
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
}
